/**
 * ビューの表示状態と「最後に選んだビュー」の記憶（端末ごと）。
 *
 * docs/01-requirements.md §5.6 P1（表示状態の記憶）・§5.17（ビュー切り替え）・§5.1（起動画面）。
 *
 * 設計上の要点:
 * - **絞り込みと並び順はビュー間で共有する**（ADR-030）。ビューを切り替えても見ている対象が
 *   変わらないようにするため。ビュー固有の記憶を増やさないので、片方のビューを畳んでも
 *   残る設定が壊れない。
 * - localStorage が使えない環境でも閲覧は続行できること。読み書きは常に既定値へ倒す。
 * - 解析・直列化は純粋関数として分けてある（localStorage の無いテスト環境で固定するため）。
 */

import type { NoteSort } from '@shared/types';

/** ビューの識別子。増やすときは `VIEW_LABEL` と `viewPath()` も一緒に足すこと。 */
export type ViewName = 'timeline' | 'list';

export interface ViewState {
  sort: NoteSort;
  categoryId: number | null;
}

/**
 * 表示状態の記憶先。
 * **この文字列を変えない。** 変えると既存端末に記憶済みの絞り込みが消える。
 * 名前に timeline が残っているのは歴史的な理由で、現在は全ビュー共有の状態を指す。
 */
const VIEW_STATE_STORAGE_KEY = 'mamemo6.timeline.view';
/** 最後に選んだビューの記憶先。 */
const VIEW_NAME_STORAGE_KEY = 'mamemo6.view';

const DEFAULT_VIEW_STATE: ViewState = { sort: 'subject', categoryId: null };
const DEFAULT_VIEW_NAME: ViewName = 'timeline';

export const VIEW_LABEL: Record<ViewName, string> = {
  timeline: 'タイムライン',
  list: '一覧',
};

/** ビューの実体となるパス。`/` は記憶したビューへの転送口でしかない。 */
export function viewPath(view: ViewName): string {
  return view === 'list' ? '/list' : '/timeline';
}

// ---------------------------------------------------------------------------
// 純粋関数（localStorage に触らない。テストはここに当てる）
// ---------------------------------------------------------------------------

export function parseViewState(raw: string | null): ViewState {
  if (!raw) return { ...DEFAULT_VIEW_STATE };
  try {
    const parsed = JSON.parse(raw) as Partial<ViewState>;
    const sort: NoteSort =
      parsed.sort === 'created' || parsed.sort === 'updated' ? parsed.sort : 'subject';
    const categoryId = typeof parsed.categoryId === 'number' ? parsed.categoryId : null;
    return { sort, categoryId };
  } catch {
    // 壊れた記憶より既定値のほうがよい。閲覧を止めない。
    return { ...DEFAULT_VIEW_STATE };
  }
}

export function serializeViewState(view: ViewState): string {
  return JSON.stringify(view);
}

export function parseViewName(raw: string | null): ViewName {
  return raw === 'list' || raw === 'timeline' ? raw : DEFAULT_VIEW_NAME;
}

// ---------------------------------------------------------------------------
// localStorage を被せた薄い層
// ---------------------------------------------------------------------------

export function loadViewState(): ViewState {
  try {
    return parseViewState(localStorage.getItem(VIEW_STATE_STORAGE_KEY));
  } catch {
    // localStorage が使えなくても閲覧はできる。
    return { ...DEFAULT_VIEW_STATE };
  }
}

export function saveViewState(view: ViewState): void {
  try {
    localStorage.setItem(VIEW_STATE_STORAGE_KEY, serializeViewState(view));
  } catch {
    /* 記憶できなくても続行する */
  }
}

export function loadViewName(): ViewName {
  try {
    return parseViewName(localStorage.getItem(VIEW_NAME_STORAGE_KEY));
  } catch {
    return DEFAULT_VIEW_NAME;
  }
}

export function saveViewName(view: ViewName): void {
  try {
    localStorage.setItem(VIEW_NAME_STORAGE_KEY, view);
  } catch {
    /* 記憶できなくても続行する */
  }
}
